import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { closetAPI, styleAPI } from '../utils/api';
import RecommendationCards from '../components/RecommendationCards';
import '../styles/Home.css';

import CheckroomIcon from '@mui/icons-material/CheckroomRounded';
import ChatIcon from '@mui/icons-material/ChatBubbleRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeRounded';
import HistoryIcon from '@mui/icons-material/HistoryRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import SocialIcon from '@mui/icons-material/Diversity3Rounded';
import InventoryIcon from '@mui/icons-material/Inventory2Rounded';
import PaletteIcon from '@mui/icons-material/PaletteRounded';
import BoltIcon from '@mui/icons-material/BoltRounded';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [closetSummary, setClosetSummary] = useState({ total: 0, categories: {} });
  const [stylePreferenceCount, setStylePreferenceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      const [closetResponse, styleResponse] = await Promise.all([
        closetAPI.getItems(),
        styleAPI.getPreferences().catch(() => null),
      ]);

      const items = closetResponse?.data?.data?.items || closetResponse?.data?.items || [];
      const categories = {};
      items.forEach((item) => {
        const category = item.category || 'Uncategorized';
        categories[category] = (categories[category] || 0) + 1;
      });

      const preferences = styleResponse?.data?.data || [];

      setClosetSummary({
        total: items.length,
        categories,
      });
      setStylePreferenceCount(preferences.length || 0);
      setLastSyncedAt(new Date());
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const topCategories = useMemo(
    () =>
      Object.entries(closetSummary.categories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
    [closetSummary.categories]
  );

  const categoryCoverage = Object.keys(closetSummary.categories).length;
  const firstName = (user?.name || user?.username || 'there').split(' ')[0];

  const features = [
    {
      title: 'Smart Closet',
      desc: 'Organize your wardrobe and retrieve looks faster.',
      icon: <CheckroomIcon />,
      link: '/closet',
      metric: `${closetSummary.total} items`,
    },
    {
      title: 'AI Stylist',
      desc: 'Get recommendations tuned to your closet and goals.',
      icon: <ChatIcon />,
      link: '/chat',
      metric: 'Live guidance',
    },
    {
      title: 'Style Profile',
      desc: 'Define preferences to sharpen AI recommendations.',
      icon: <AutoAwesomeIcon />,
      link: '/style-profile',
      metric: `${stylePreferenceCount} preferences`,
    },
    {
      title: 'Social Feed',
      desc: 'Explore outfits from people with a similar vibe.',
      icon: <SocialIcon />,
      link: '/social',
      metric: 'Discover looks',
    },
    {
      title: 'Outfit History',
      desc: 'Track what you wore and spot repeat winners.',
      icon: <HistoryIcon />,
      link: '/outfit-history',
      metric: 'Build memory',
    },
  ];

  const dashboardTips = [
    'Anchor every look with one core item, then build around it.',
    'Plan outfits in batches for the week to remove daily friction.',
    'Save your strongest combinations in outfit history for quick reuse.',
  ];

  return (
    <div className="home-shell">
      <section className="home-hero glass-panel-heavy animate-slide-up">
        <div className="hero-copy">
          <p className="hero-kicker">Good to see you, {firstName}</p>
          <h2 className="hero-title">Your styling workspace is ready.</h2>
          <p className="hero-subtitle">
            Build outfits faster, keep your closet organized, and use AI as your daily style copilot.
          </p>
          <div className="hero-actions">
            <Link to="/chat" className="btn-primary">
              Ask AI Stylist
              <ArrowForwardIcon fontSize="small" />
            </Link>
            <Link to="/closet" className="btn-ghost">
              Open Closet
            </Link>
            <Link to="/chat?prompt=Build%20a%20grounded%20outfit%20from%20my%20closet%20for%20today." className="btn-soft">
              Dress me today
            </Link>
            <Link to="/chat?prompt=Plan%20five%20grounded%20outfits%20for%20my%20week%20and%20avoid%20unnecessary%20repeats." className="btn-soft">
              Plan my week
            </Link>
          </div>
        </div>

        <div className="hero-meta">
          <div className="hero-meta-card">
            <InventoryIcon fontSize="small" />
            <div>
              <p className="meta-label">Closet Volume</p>
              <p className="meta-value">{loading ? '--' : closetSummary.total}</p>
            </div>
          </div>
          <div className="hero-meta-card">
            <PaletteIcon fontSize="small" />
            <div>
              <p className="meta-label">Category Mix</p>
              <p className="meta-value">{loading ? '--' : categoryCoverage}</p>
            </div>
          </div>
          <div className="hero-meta-card">
            <BoltIcon fontSize="small" />
            <div>
              <p className="meta-label">Style Signals</p>
              <p className="meta-value">{loading ? '--' : stylePreferenceCount}</p>
            </div>
          </div>
          <p className="sync-note">
            {lastSyncedAt ? `Last synced ${lastSyncedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Sync pending'}
          </p>
        </div>
      </section>

      <section className="animate-fade-in" style={{ animationDelay: '0.04s' }}>
        <RecommendationCards />
      </section>

      <section className="workspace-grid animate-fade-in" style={{ animationDelay: '0.06s' }}>
        {features.map((feature) => (
          <button key={feature.title} className="workspace-card glass-panel" onClick={() => navigate(feature.link)}>
            <div className="workspace-icon">{feature.icon}</div>
            <p className="workspace-metric">{feature.metric}</p>
            <h3>{feature.title}</h3>
            <p>{feature.desc}</p>
            <span className="workspace-link">
              Open <ArrowForwardIcon fontSize="small" />
            </span>
          </button>
        ))}
      </section>

      <section className="insights-grid animate-fade-in" style={{ animationDelay: '0.12s' }}>
        <div className="insight-panel glass-panel">
          <div className="insight-header">
            <h3>Top Categories</h3>
            <p>Where your current wardrobe is strongest.</p>
          </div>

          {loading ? (
            <div className="loading-pulse" style={{ height: 94, borderRadius: 14 }} />
          ) : topCategories.length === 0 ? (
            <div className="empty-note">
              <p>No items yet. Start by adding your first piece to the closet.</p>
              <Link to="/closet/add" className="btn-primary">
                Add First Item
              </Link>
            </div>
          ) : (
            <div className="category-breakdown">
              {topCategories.map(([category, count]) => {
                const width = closetSummary.total > 0 ? Math.max(8, (count / closetSummary.total) * 100) : 0;
                return (
                  <div key={category} className="category-row">
                    <div className="category-line">
                      <span>{category}</span>
                      <strong>{count}</strong>
                    </div>
                    <div className="category-track">
                      <div className="category-fill" style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="insight-panel glass-panel">
          <div className="insight-header">
            <h3>Styling Playbook</h3>
            <p>Small moves that improve outcomes immediately.</p>
          </div>

          <div className="tips-list">
            {dashboardTips.map((tip) => (
              <div key={tip} className="tip-item">
                <span className="tip-dot" />
                <p>{tip}</p>
              </div>
            ))}
          </div>

          <Link to="/outfit-history" className="btn-ghost tips-action">
            Review Outfit History
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
